<template>
  <v-container fluid>
    <!-- Listado de plantillas -->
    <v-card v-if="!editing" class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-text-box-multiple-outline</v-icon>
        <v-toolbar-title>Plantillas de comodato</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="openCreate" title="Nueva plantilla"><v-icon>mdi-plus</v-icon></v-btn>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pb-0">
        <v-alert type="info" text dense>
          La plantilla predeterminada es la que usan los dealers del grupo. Mientras no marques ninguna se usa la de Pivot Fleet.
          Un borrador no lo ve ningún dealer hasta que se publica.
        </v-alert>
      </v-card-text>

      <v-data-table :headers="headers" :items="templates" :loading="loading" item-key="id"
        :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }" no-data-text="No hay plantillas">
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
          <div class="caption grey--text" v-if="item.title">{{ item.title }}</div>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small v-if="item.is_system" color="grey lighten-2" class="mr-1">Pivot Fleet</v-chip>
          <v-chip x-small v-if="item.is_published" color="green" text-color="white" class="mr-1">Publicada</v-chip>
          <v-chip x-small v-else color="orange" text-color="white" class="mr-1">Borrador</v-chip>
          <v-chip x-small v-if="item.is_default" color="primary" class="mr-1">
            <v-icon x-small left>mdi-star</v-icon>Predeterminada
          </v-chip>
        </template>
        <template v-slot:item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn icon small color="primary" @click="openEditor(item)" title="Ver / editar"><v-icon>mdi-pencil</v-icon></v-btn>
          <v-btn icon small @click="duplicate(item)" title="Duplicar"><v-icon>mdi-content-copy</v-icon></v-btn>
          <v-btn icon small v-if="!item.is_system && !item.is_published" color="green" @click="publish(item)" title="Publicar">
            <v-icon>mdi-publish</v-icon>
          </v-btn>
          <v-btn icon small v-if="!item.is_system && item.is_published" :disabled="item.is_default" @click="unpublish(item)"
            :title="item.is_default ? 'Marca otra como predeterminada antes de despublicar' : 'Despublicar'">
            <v-icon>mdi-publish-off</v-icon>
          </v-btn>
          <v-btn icon small v-if="item.is_published && !item.is_default" color="amber darken-2" @click="setDefault(item)"
            title="Marcar como predeterminada">
            <v-icon>mdi-star-outline</v-icon>
          </v-btn>
          <v-btn icon small v-if="!item.is_system" :disabled="item.is_default" color="error" @click="remove(item)"
            :title="item.is_default ? 'Marca otra como predeterminada antes de eliminar' : 'Eliminar'">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Editor de la plantilla -->
    <v-card v-else class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-btn icon @click="closeEditor" title="Volver"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <v-toolbar-title>{{ editing.name || 'Plantilla' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip v-if="dirty" small color="orange" text-color="white" class="mr-3">Cambios sin guardar</v-chip>
        <v-btn outlined class="mr-2" :disabled="!clauses.length" @click="openPreview">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Vista previa / PDF
        </v-btn>
        <v-btn outlined :loading="saving" :disabled="saving || !dirty" @click="save">
          <v-icon left small>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert v-if="editing.is_system" type="warning" text dense>
          Esta es la plantilla de Pivot Fleet. Al guardar se creará una copia para tu grupo y seguirás editando esa copia.
        </v-alert>
        <v-alert type="warning" outlined dense icon="mdi-alert-circle-outline">
          El contenido de las cláusulas editables y personalizadas es responsabilidad de la concesión y del grupo. Pivot Fleet no realiza revisión legal de dicho contenido.
        </v-alert>
        <v-alert type="info" text dense>
          Los cambios solo afectan a comodatos aún sin firmar y a los nuevos. Los ya firmados conservan el clausulado que tenían.
        </v-alert>

        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field v-model="editing.name" label="Nombre *" outlined dense hide-details="auto"
              :rules="[v => !!v || 'El nombre es obligatorio']" @input="dirty = true"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="editing.title" label="Título del documento" outlined dense hide-details="auto"
              @input="dirty = true"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="editing.subtitle" label="Subtítulo" outlined dense hide-details="auto"
              @input="dirty = true"></v-text-field>
          </v-col>
        </v-row>

        <div class="subtitle-1 font-weight-medium mt-4 mb-2">
          Cláusulas ({{ clauses.length }}/{{ MAX_CLAUSES }})
        </div>

        <div v-if="!clauses.length" class="pa-6 text-center grey--text">
          <v-icon size="48" color="grey lighten-1">mdi-text-box-remove-outline</v-icon>
          <div class="mt-2">Lienzo en blanco. Añade la primera cláusula.</div>
        </div>

        <draggable v-model="clauses" handle=".drag-handle" ghost-class="clause-ghost" animation="200" @end="dirty = true">
          <v-card v-for="(clause, index) in clauses" :key="clause.key" outlined class="mb-2">
            <div class="d-flex align-start pa-3">
              <div class="drag-handle mr-2 mt-1" style="cursor: grab;">
                <v-icon small color="grey">mdi-drag-vertical</v-icon>
              </div>
              <v-avatar size="28" color="grey lighten-3" class="mr-3 mt-1 flex-shrink-0">
                <span class="caption font-weight-medium">{{ index + 1 }}</span>
              </v-avatar>
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="d-flex align-center flex-wrap mb-1">
                  <span class="subtitle-2 font-weight-medium mr-2">{{ clause.title }}</span>
                  <v-chip x-small class="mr-1" :color="typeColor(clause.type)">
                    <v-icon x-small left v-if="clause.type === 'fija'">mdi-lock</v-icon>
                    {{ typeLabel(clause.type) }}
                  </v-chip>
                  <v-chip x-small v-if="clause.type === 'opcional'" :color="clause.enabled_by_default ? 'green' : 'grey'"
                    text-color="white">
                    {{ clause.enabled_by_default ? 'Activada por defecto' : 'Desactivada por defecto' }}
                  </v-chip>
                </div>
                <div class="body-2 grey--text text--darken-2 clause-text" v-html="renderText(clause.text)"></div>
                <div class="mt-1">
                  <v-btn x-small text color="primary" @click="openClause(clause)">
                    <v-icon x-small left>mdi-pencil</v-icon>Editar
                  </v-btn>
                  <v-btn x-small text color="error" @click="removeClause(clause)">
                    <v-icon x-small left>mdi-delete</v-icon>Eliminar
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card>
        </draggable>

        <v-btn text block color="primary" class="mt-2" :disabled="clauses.length >= MAX_CLAUSES" @click="openClause(null)">
          <v-icon left>mdi-plus</v-icon>
          Añadir cláusula
        </v-btn>
        <div v-if="clauses.length >= MAX_CLAUSES" class="caption grey--text text-center mt-2">
          Se ha alcanzado el límite de {{ MAX_CLAUSES }} cláusulas por plantilla.
        </div>

        <comodato-signatures-block></comodato-signatures-block>
      </v-card-text>
    </v-card>

    <!-- Vista previa del documento (con los cambios aún sin guardar) -->
    <v-dialog v-model="previewDialog" max-width="820px" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-file-document-outline</v-icon>
          Vista previa del documento
          <v-spacer></v-spacer>
          <v-btn icon dark @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 70vh;">
          <v-alert type="info" text dense>
            Incluye los cambios sin guardar y usa datos de ejemplo. Las cláusulas opcionales aparecen según su valor por defecto.
            Para obtener el PDF pulsa "Imprimir / guardar PDF" y elige "Guardar como PDF".
          </v-alert>
          <div class="preview-doc" v-html="previewHtml"></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="previewDialog = false">Cerrar</v-btn>
          <v-btn color="primary" @click="printPreview">
            <v-icon left small>mdi-printer</v-icon>
            Imprimir / guardar PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Nueva plantilla -->
    <v-dialog v-model="createDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-text-box-plus-outline</v-icon>
          Nueva plantilla
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="createForm" v-model="createValid">
            <v-text-field v-model="createForm.name" label="Nombre *" outlined dense :rules="[v => !!v || 'El nombre es obligatorio']"></v-text-field>
            <v-text-field v-model="createForm.title" label="Título del documento" outlined dense></v-text-field>
            <v-text-field v-model="createForm.subtitle" label="Subtítulo" outlined dense hide-details></v-text-field>
          </v-form>
          <div class="caption grey--text mt-3">Se crea como borrador y sin cláusulas. Para partir de la de Pivot Fleet, duplícala desde el listado.</div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!createValid" :loading="saving" @click="create">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Editor de una cláusula -->
    <v-dialog v-model="clauseDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-format-list-numbered</v-icon>
          {{ clauseForm.key ? 'Editar cláusula' : 'Nueva cláusula' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-select v-model="clauseForm.type" :items="typeItems" label="Tipo *" outlined dense hide-details="auto"></v-select>
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field v-model="clauseForm.title" label="Título *" outlined dense counter="255" hide-details="auto"
                :rules="[v => !!v || 'El título es obligatorio', v => (v && v.length <= 255) || 'Máximo 255 caracteres']"></v-text-field>
            </v-col>
          </v-row>
          <variable-editor ref="clauseText" v-model="clauseForm.text" label="Texto" :maxlength="5000" required
            class="mt-4" min-height="140px"></variable-editor>

          <v-menu v-model="variableMenu" :close-on-content-click="false" offset-y max-height="400" max-width="350">
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small text color="amber darken-3" class="mt-2" v-bind="attrs" v-on="on">
                <v-icon x-small left>mdi-code-braces</v-icon>
                Insertar dato
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="py-2 subtitle-2">Variables automatizadas</v-card-title>
              <v-divider></v-divider>
              <v-list dense class="py-0">
                <template v-for="group in variables">
                  <v-subheader :key="'h-' + group.category" class="font-weight-bold">{{ group.category }}</v-subheader>
                  <v-list-item v-for="variable in group.variables" :key="variable.marker" dense @click="insertVariable(variable)">
                    <v-list-item-content>
                      <v-list-item-title class="body-2">{{ variable.name }}</v-list-item-title>
                      <v-list-item-subtitle class="caption">Ej: {{ variable.example }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action><v-icon small color="amber darken-2">mdi-plus-circle</v-icon></v-list-item-action>
                  </v-list-item>
                </template>
              </v-list>
            </v-card>
          </v-menu>

          <v-switch v-if="clauseForm.type === 'opcional'" v-model="clauseForm.enabled_by_default" dense hide-details class="mt-2"
            label="Activada por defecto para los dealers"></v-switch>
          <div v-if="clauseForm.type === 'personalizable'" class="caption grey--text mt-2">
            Personalizable: el dealer puede reescribir el texto (no el título) y restaurar el tuyo.
          </div>
          <div class="caption grey--text mt-2">
            Como Administrador de Grupo puedes editar el texto de cualquier tipo de cláusula, incluidas las fijas.
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="clauseDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="applyClause">Aplicar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import VariableEditor from '@/components/VariableEditor'
import ComodatoSignaturesBlock from '@/components/ComodatoSignaturesBlock'
import moment from 'moment'
import GrupoComodatoService from '@/services/GrupoComodatoService'
import ComodatoClauseService from '@/services/ComodatoClauseService'
import { apiData, apiError } from '@/grupo'

const MAX_CLAUSES = 50

const emptyClause = () => ({ key: null, id: null, type: 'editable', title: '', text: '', enabled_by_default: true })

export default {
  name: 'GrupoComodato',

  components: { draggable, VariableEditor, ComodatoSignaturesBlock },

  data: () => ({
    MAX_CLAUSES,
    templates: [],
    loading: false,
    saving: false,

    // plantilla abierta en el editor (con sus cláusulas en `clauses`)
    editing: null,
    clauses: [],
    dirty: false,
    nextKey: 1,

    createDialog: false,
    createValid: false,
    createForm: { name: '', title: '', subtitle: '' },

    clauseDialog: false,
    clauseForm: emptyClause(),
    variableMenu: false,
    variables: [],
    previewDialog: false,
    previewHtml: '',

    headers: [
      { text: 'Plantilla', value: 'name' },
      { text: 'Estado', value: 'status', sortable: false },
      { text: 'Cláusulas', value: 'clauses_count' },
      { text: 'Actualizada', value: 'updated_at' },
      { text: '', value: 'action', sortable: false, align: 'end' }
    ],
    typeItems: [
      { text: 'Fija', value: 'fija' },
      { text: 'Editable', value: 'editable' },
      { text: 'Personalizable', value: 'personalizable' },
      { text: 'Opcional', value: 'opcional' }
    ]
  }),

  mounted () {
    this.load()
  },

  methods: {
    formatDate (value) {
      return value ? moment.unix(value).format('DD/MM/YYYY HH:mm') : '—'
    },

    async load () {
      this.loading = true
      try {
        this.templates = apiData(await GrupoComodatoService.list()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar las plantillas'))
      } finally {
        this.loading = false
      }
    },

    // ---- acciones del listado ----
    openCreate () {
      this.createForm = { name: '', title: '', subtitle: '' }
      this.createDialog = true
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.resetValidation())
    },

    async create () {
      this.saving = true
      try {
        const payload = { name: this.createForm.name }
        if (this.createForm.title) payload.title = this.createForm.title
        if (this.createForm.subtitle) payload.subtitle = this.createForm.subtitle
        const template = apiData(await GrupoComodatoService.create(payload))
        this.createDialog = false
        this.$toast.success('Plantilla creada como borrador')
        this.load()
        this.startEditing(template)
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo crear la plantilla'))
      } finally {
        this.saving = false
      }
    },

    async duplicate (item) {
      try {
        await GrupoComodatoService.duplicate(item.id)
        this.$toast.success('Plantilla duplicada como borrador')
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo duplicar la plantilla'))
      }
    },

    async publish (item) {
      try {
        await GrupoComodatoService.publish(item.id)
        this.$toast.success('Plantilla publicada')
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo publicar la plantilla'))
      }
    },

    async unpublish (item) {
      const ok = await this.$confirm(`¿Despublicar "${item.name}"? Dejará de estar disponible para los dealers.`, { title: 'Despublicar plantilla' })
      if (!ok) return
      try {
        await GrupoComodatoService.unpublish(item.id)
        this.$toast.success('Plantilla despublicada')
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo despublicar la plantilla'))
      }
    },

    async setDefault (item) {
      const ok = await this.$confirm(
        `¿Marcar "${item.name}" como predeterminada? Los textos y activaciones que los dealers habían puesto sobre la plantilla anterior ` +
        'no se trasladan a la nueva (sus cláusulas personalizadas sí se conservan). Los comodatos ya firmados no cambian.',
        { title: 'Cambiar plantilla predeterminada' }
      )
      if (!ok) return
      try {
        this.templates = apiData(await GrupoComodatoService.setDefault(item.id)) || this.templates
        this.$toast.success('Plantilla predeterminada actualizada')
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo marcar como predeterminada'))
      }
    },

    async remove (item) {
      const ok = await this.$confirm(`¿Eliminar la plantilla "${item.name}"?`, { title: 'Eliminar plantilla' })
      if (!ok) return
      try {
        await GrupoComodatoService.remove(item.id)
        this.$toast.success('Plantilla eliminada')
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo eliminar la plantilla'))
      }
    },

    // ---- editor de plantilla ----
    async openEditor (item) {
      try {
        this.startEditing(apiData(await GrupoComodatoService.view(item.id)))
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cargar la plantilla'))
      }
    },

    startEditing (template) {
      const { clauses, ...info } = template
      this.editing = { ...info }
      this.clauses = (clauses || []).map(c => this.toLocalClause(c))
      this.dirty = false
      if (!this.variables.length) this.loadVariables()
    },

    toLocalClause (c) {
      return {
        key: 'k' + this.nextKey++,
        id: c.id || null,
        type: c.type,
        title: c.title,
        text: c.text,
        enabled_by_default: c.enabled_by_default !== undefined ? !!c.enabled_by_default : true
      }
    },

    async closeEditor () {
      if (this.dirty) {
        const ok = await this.$confirm('Hay cambios sin guardar. ¿Salir sin guardarlos?', { title: 'Cambios sin guardar' })
        if (!ok) return
      }
      this.editing = null
      this.clauses = []
      this.dirty = false
    },

    async save () {
      if (!this.editing.name || !this.editing.name.trim()) {
        this.$toast.error('El nombre es obligatorio')
        return
      }
      this.saving = true
      try {
        const payload = {
          name: this.editing.name,
          title: this.editing.title,
          subtitle: this.editing.subtitle,
          clauses: this.clauses.map(c => {
            const item = { type: c.type, title: c.title, text: c.text }
            if (c.id) item.id = c.id
            if (c.type === 'opcional') item.enabled_by_default = !!c.enabled_by_default
            return item
          })
        }
        const wasSystem = this.editing.is_system
        const saved = apiData(await GrupoComodatoService.update(this.editing.id, payload))
        // Sobre la de Pivot Fleet la API devuelve una copia del grupo con otro id: se pasa a editar esa
        this.startEditing(saved.clauses ? saved : apiData(await GrupoComodatoService.view(saved.id)))
        this.$toast.success(wasSystem ? 'Se ha creado una copia de la plantilla para tu grupo' : 'Plantilla guardada')
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo guardar la plantilla'))
      } finally {
        this.saving = false
      }
    },

    // ---- cláusulas ----
    async loadVariables () {
      try {
        this.variables = apiData(await ComodatoClauseService.getVariables()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar las variables'))
      }
    },

    openClause (clause) {
      this.clauseForm = clause ? { ...clause } : emptyClause()
      this.variableMenu = false
      this.clauseDialog = true
    },

    applyClause () {
      const form = this.clauseForm
      if (!form.title || !form.title.trim()) return this.$toast.error('El título es obligatorio')
      if (form.title.length > 255) return this.$toast.error('El título admite 255 caracteres como máximo')
      if (!form.text || !form.text.trim()) return this.$toast.error('El texto es obligatorio')
      if (form.text.length > 5000) return this.$toast.error('El texto admite 5000 caracteres como máximo')

      if (form.key) {
        const index = this.clauses.findIndex(c => c.key === form.key)
        if (index > -1) this.clauses.splice(index, 1, { ...form })
      } else {
        this.clauses.push({ ...form, key: 'k' + this.nextKey++ })
      }
      this.dirty = true
      this.clauseDialog = false
    },

    removeClause (clause) {
      this.clauses = this.clauses.filter(c => c.key !== clause.key)
      this.dirty = true
    },

    // Inserta la variable como chip en la posición del cursor
    insertVariable (variable) {
      if (this.$refs.clauseText) {
        this.$refs.clauseText.insertVariable(variable.marker)
      } else {
        this.clauseForm.text = (this.clauseForm.text || '') + variable.marker
      }
      this.variableMenu = false
    },
    // ---- vista previa / PDF ----
    async openPreview () {
      if (!this.variables.length) await this.loadVariables()
      this.previewHtml = this.buildPreviewHtml()
      this.previewDialog = true
    },
    buildPreviewHtml () {
      const examples = {}
      this.variables.forEach(g => (g.variables || []).forEach(v => { examples[v.marker] = v.example }))
      const esc = t => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const fill = text => esc(text || '')
        .replace(/\{\{[^}]+\}\}/g, m => {
          const raw = m.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
          return examples[raw] !== undefined ? esc(examples[raw]) : m
        })
        .replace(/\n/g, '<br>')
      const items = this.clauses
        .filter(c => c.type !== 'opcional' || c.enabled_by_default)
        .map(c => `<li><strong>${esc(c.title)}:</strong> ${fill(c.text)}</li>`)
        .join('')
      const title = this.editing.title ? `<h2>${esc(this.editing.title)}</h2>` : ''
      const subtitle = this.editing.subtitle ? `<h3>${esc(this.editing.subtitle)}</h3>` : ''
      const signatures = '<h4>Firmas</h4><p><strong>Entrega:</strong> Cedente (sello del dealer) y Cesionario (firma del conductor, y segundo conductor si lo hay).<br>' +
        '<strong>Devolución:</strong> Cedente y Cesionario, firmadas al recibir el vehículo.</p>'
      return `${title}${subtitle}<ol>${items}</ol>${signatures}`
    },
    printPreview () {
      const win = window.open('', '_blank')
      if (!win) {
        this.$toast.warning('El navegador bloqueó la ventana. Permite las ventanas emergentes para imprimir.')
        return
      }
      win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${this.editing.name || 'Comodato'}</title>
        <style>body{font-family:Arial,sans-serif;font-size:12px;line-height:1.5;margin:32px;color:#000}
        h2,h3{text-align:center;margin:4px 0 12px}li{margin-bottom:10px;text-align:justify}</style></head>
        <body>${this.previewHtml}</body></html>`)
      win.document.close()
      win.focus()
      win.print()
    },
    renderText (text) {
      if (!text) return ''
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
        .replace(/\{\{([^}]+)\}\}/g, '<span class="variable-chip">$1</span>')
    },

    typeLabel (type) {
      const found = this.typeItems.find(t => t.value === type)
      return found ? found.text : type
    },

    typeColor (type) {
      return { fija: 'grey lighten-2', editable: 'blue lighten-4', personalizable: 'purple lighten-4', opcional: 'green lighten-4' }[type] || 'grey lighten-2'
    }
  }
}
</script>

<style scoped>
.clause-ghost {
  opacity: 0.4;
  background: #e3f2fd;
}

.clause-text {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 120px;
  overflow: hidden;
}

.drag-handle:hover .v-icon {
  color: var(--v-primary-base, #1976d2) !important;
}

.preview-doc {
  font-size: 0.875rem;
  line-height: 1.6;
}
.preview-doc >>> h2,
.preview-doc >>> h3 {
  text-align: center;
  margin-bottom: 12px;
}
.preview-doc >>> ol {
  padding-left: 20px;
}
.preview-doc >>> li {
  margin-bottom: 12px;
  text-align: justify;
}
.clause-text >>> .variable-chip {
  background-color: #fff3e0;
  color: #e65100;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  border: 1px solid #ffe0b2;
  white-space: nowrap;
}
</style>
